/**
 * Drive Config — Singleton for bigwa-config.json lifecycle
 *
 * Manages persistent folder IDs in appDataFolder.
 * Three states: 'ready' | 'recovery-needed' | 'degraded'
 *
 * Phase 1: config is written opportunistically after folder discovery.
 * If scope is absent or init fails, app continues normally (degraded).
 */

import { hasAppDataScope, readSystemFile, writeSystemFile, resetScopeCache } from './driveResilience';
import { AppError } from '../core/errors/AppError';
import { logger } from '../utils/logger';

// ============================================================================
// TYPES
// ============================================================================

export type DriveInitState = 'ready' | 'recovery-needed' | 'degraded';

export type DegradedReason = 'no-scope' | 'error' | null;

export interface BigwaConfigFiles {
  subscriptionJson?: string;
  scratchpadNotesJson?: string;
  namingConfigJson?: string;
  referenciasJson?: string;
  collectionsJson?: string;
}

export interface BigwaConfig {
  version: 1;
  rootFolderId: string;
  folders: {
    biblioteca: string;
    repositorio: string;
    projetos: string;
    scratchpad?: string;
    config?: string;
  };
  files?: BigwaConfigFiles;
  updatedAt: string;
}

const CONFIG_FILENAME = 'bigwa-config.json';

// ============================================================================
// CONFIG ERROR
// ============================================================================

class ConfigNotReadyError extends AppError {
  readonly code = 'CONFIG_NOT_READY';
  readonly category = 'internal' as const;
  readonly severity = 'medium' as const;
  readonly userMessage = 'Configuração do Drive não inicializada.';
  readonly recoverable = true;

  constructor() {
    super('Drive config not initialized');
  }
}

// ============================================================================
// SINGLETON
// ============================================================================

class DriveConfigService {
  private config: BigwaConfig | null = null;
  private state: DriveInitState = 'degraded';
  private degradedReason: DegradedReason = null;
  private listeners: Set<() => void> = new Set();

  /**
   * Initialize resilience config by persisting folder IDs to appDataFolder.
   *
   * Called from googleDrive/core.ts after folder structure is confirmed.
   * If scope is missing or any error occurs, state = 'degraded' (silent).
   */
  async initialize(folderIds: {
    root: string;
    biblioteca: string;
    repositorio: string;
    projetos: string;
  }): Promise<DriveInitState> {
    try {
      // 1. Check scope
      const hasScope = await hasAppDataScope();
      if (!hasScope) {
        this.state = 'degraded';
        this.degradedReason = 'no-scope';
        logger.info('DriveConfig', 'appDataFolder scope not available — degraded (expected for existing users)');
        this.notifyListeners();
        return this.state;
      }

      // 2. Try to read existing config
      const existing = await readSystemFile(CONFIG_FILENAME) as BigwaConfig | null;

      if (existing && this.configMatchesFolderIds(existing, folderIds)) {
        // Config exists and IDs match — ready
        this.config = existing;
        this.state = 'ready';
        this.degradedReason = null;
        logger.info('DriveConfig', 'Config loaded — ready');
        this.notifyListeners();
        return this.state;
      }

      // 3. Config missing or IDs diverged — write new config
      const newConfig: BigwaConfig = {
        version: 1,
        rootFolderId: folderIds.root,
        folders: {
          biblioteca: folderIds.biblioteca,
          repositorio: folderIds.repositorio,
          projetos: folderIds.projetos,
        },
        updatedAt: new Date().toISOString(),
      };

      await writeSystemFile(CONFIG_FILENAME, newConfig);
      this.config = newConfig;
      this.state = 'ready';
      this.degradedReason = null;
      logger.info('DriveConfig', 'Config written — ready');
      this.notifyListeners();
      return this.state;
    } catch (error) {
      this.state = 'degraded';
      this.degradedReason = 'error';
      logger.warn('DriveConfig', 'Init failed — degraded:', error);
      this.notifyListeners();
      return this.state;
    }
  }

  /**
   * Preload config from appDataFolder (read-only).
   * Makes folder IDs available as hints for ID-first discovery.
   * Does NOT change state — stays 'degraded' until initialize() runs.
   */
  async preload(): Promise<boolean> {
    try {
      const hasScope = await hasAppDataScope();
      if (!hasScope) return false;

      const existing = await readSystemFile(CONFIG_FILENAME) as BigwaConfig | null;
      if (!existing || existing.version !== 1) return false;

      this.config = existing;
      logger.info('DriveConfig', 'Config preloaded (IDs available as hints)');
      return true;
    } catch (error) {
      logger.warn('DriveConfig', 'Preload failed:', error);
      return false;
    }
  }

  /**
   * Get preloaded root folder ID. Returns undefined if config not loaded.
   */
  getRootFolderId(): string | undefined {
    return this.config?.rootFolderId;
  }

  /**
   * Get preloaded subfolder ID by Drive folder name.
   * Maps Drive names to config keys.
   */
  getSubfolderId(driveName: string): string | undefined {
    if (!this.config) return undefined;
    const mapping: Record<string, keyof BigwaConfig['folders']> = {
      'Biblioteca': 'biblioteca',
      'Repositorio': 'repositorio',
      'Projetos': 'projetos',
      'Scratchpad': 'scratchpad',
      'Config': 'config',
    };
    const key = mapping[driveName];
    if (!key) return undefined;
    return this.config.folders[key];
  }

  /**
   * Get the current config. THROWS if not initialized (CLAUDE.md rule).
   * Never use getConfig()?.campo — the ?. won't work because this throws.
   */
  getConfig(): BigwaConfig {
    if (!this.config) {
      throw new ConfigNotReadyError();
    }
    return this.config;
  }

  getState(): DriveInitState {
    return this.state;
  }

  getDegradedReason(): DegradedReason {
    return this.degradedReason;
  }

  // ---- Phase 2a: file & folder ID registration ----

  /**
   * Get cached file ID. Returns undefined if config absent (does NOT throw).
   * In degraded mode this returns undefined, causing callers to use name-based fallback.
   */
  getFileId(key: keyof BigwaConfigFiles): string | undefined {
    return this.config?.files?.[key];
  }

  /**
   * Get cached folder ID for lazy folders (scratchpad, config).
   * Returns undefined if not cached.
   */
  getFolderId(key: 'scratchpad' | 'config'): string | undefined {
    return this.config?.folders?.[key];
  }

  /**
   * Register a file ID in the config. Persists to appDataFolder.
   * No-op if state !== 'ready'. Fire-and-forget safe.
   */
  async registerFileId(key: keyof BigwaConfigFiles, fileId: string): Promise<void> {
    if (this.state !== 'ready' || !this.config) return;
    if (this.config.files?.[key] === fileId) return;

    if (!this.config.files) {
      this.config.files = {};
    }
    this.config.files[key] = fileId;
    this.config.updatedAt = new Date().toISOString();

    try {
      await writeSystemFile(CONFIG_FILENAME, this.config);
      logger.info('DriveConfig', `Registered file ID: ${key}`);
    } catch (error) {
      logger.warn('DriveConfig', `Failed to persist file ID ${key}:`, error);
    }
  }

  /**
   * Register a folder ID for lazy folders (scratchpad, config).
   * No-op if state !== 'ready'. Fire-and-forget safe.
   */
  async registerFolderId(key: 'scratchpad' | 'config', folderId: string): Promise<void> {
    if (this.state !== 'ready' || !this.config) return;
    if (this.config.folders[key] === folderId) return;

    this.config.folders[key] = folderId;
    this.config.updatedAt = new Date().toISOString();

    try {
      await writeSystemFile(CONFIG_FILENAME, this.config);
      logger.info('DriveConfig', `Registered folder ID: ${key}`);
    } catch (error) {
      logger.warn('DriveConfig', `Failed to persist folder ID ${key}:`, error);
    }
  }

  /**
   * Transition to recovery-needed state.
   * Called by core.ts when preloaded root folder ID exists but folder is gone.
   */
  setRecoveryNeeded(): void {
    this.state = 'recovery-needed';
    this.degradedReason = null;
    this.notifyListeners();
  }

  /**
   * Reset all state. Called on logout.
   */
  reset(): void {
    this.config = null;
    this.state = 'degraded';
    this.degradedReason = null;
    resetScopeCache();
    this.notifyListeners();
  }

  // ---- Subscription for React (useSyncExternalStore) ----

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getSnapshot(): DriveInitState {
    return this.state;
  }

  // ---- Private ----

  private notifyListeners(): void {
    this.listeners.forEach((fn) => fn());
  }

  private configMatchesFolderIds(
    config: BigwaConfig,
    folderIds: { root: string; biblioteca: string; repositorio: string; projetos: string }
  ): boolean {
    return (
      config.rootFolderId === folderIds.root &&
      config.folders.biblioteca === folderIds.biblioteca &&
      config.folders.repositorio === folderIds.repositorio &&
      config.folders.projetos === folderIds.projetos
    );
  }
}

export const driveConfig = new DriveConfigService();
